import React from 'react'
import type { ActionItem, User } from '../../types'
import { sourceKindLabel } from '../../constants/actionItemSources'
import FloatingMenu from '../shared/FloatingMenu'
import { sortPeople } from '../../utils/peopleSort'

// Task #700: one table presentation shared by the Team Action Items page and
// the in-meeting "Accountability from Last Week" section. The column
// template, the header row and the collapsed-row cells all live here so the
// two surfaces can't drift apart visually.

/** Column template every action item row (and the header) must use. */
export const ACTION_ITEM_ROW_GRID =
  'grid min-w-[840px] grid-cols-[32px_minmax(160px,1fr)_160px_120px_120px_80px_40px] items-center gap-3 px-4'

export function formatActionItemDate(dateStr: string): string {
  const date = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Due-date colouring: completed is muted teal, overdue is red (plus a red
 * left edge on the row), due today amber, within a week full-strength text,
 * and anything further out is dimmed.
 */
export function actionItemDueDateStyle(item: ActionItem): { color: string; opacity?: number; borderLeft?: string } {
  if (item.status === 'completed') {
    return { color: '#648E8A' }
  }
  if (item.overdue) {
    return { color: '#ED1C24', borderLeft: '3px solid #ED1C24' }
  }
  if (item.due_today) {
    return { color: '#FBA737' }
  }
  const dueDate = new Date(item.due_date + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays <= 7) {
    return { color: '#1B1B1B' }
  }
  return { color: '#1B1B1B', opacity: 0.6 }
}

function HeaderLabel({ label }: { label: string }) {
  return (
    <span className="text-sm font-medium text-text-secondary uppercase tracking-wider">{label}</span>
  )
}

/** TITLE / OWNER / DUE DATE / DATE CREATED / SOURCE header row. */
export function ActionItemTableHeader() {
  return (
    <div className={`${ACTION_ITEM_ROW_GRID} py-2 border-b border-gray-200 bg-[#F8F9FA]`}>
      <div />
      <HeaderLabel label="Title" />
      <HeaderLabel label="Owner" />
      <HeaderLabel label="Due Date" />
      <HeaderLabel label="Date Created" />
      <HeaderLabel label="Source" />
      <div />
    </div>
  )
}

/** 24px owner chip: the uploaded photo when there is one, initials otherwise. */
export function ActionItemOwnerAvatar({ owner }: { owner?: User | null }) {
  if (owner?.avatar_url) {
    return (
      <img
        src={owner.avatar_url}
        alt={owner.full_name}
        title={owner.full_name}
        className="w-6 h-6 rounded-full object-cover flex-shrink-0"
      />
    )
  }
  return (
    <span className="w-6 h-6 rounded-full flex items-center justify-center bg-pinnacle-red text-white text-sm font-medium flex-shrink-0">
      {owner?.initials || '?'}
    </span>
  )
}

interface TitleCellProps {
  item: ActionItem
  /** Inline title editing. Omit the handlers to render a static title. */
  editing?: boolean
  value?: string
  onChange?: (value: string) => void
  onSave?: () => void
  onCancel?: () => void
  inputRef?: React.RefObject<HTMLInputElement>
  onStartEdit?: (e: React.MouseEvent) => void
  /** false renders the title as plain text (read-only surfaces). */
  editable?: boolean
  /** Rendered before the title, e.g. a drag handle. */
  leading?: React.ReactNode
  onInputClick?: (e: React.MouseEvent) => void
}

export function ActionItemTitleCell({
  item, editing = false, value = '', onChange, onSave, onCancel,
  inputRef, onStartEdit, editable = true, leading, onInputClick,
}: TitleCellProps) {
  const isCompleted = item.status === 'completed'
  const isCarriedOver = item.status === 'carried_over'

  return (
    <div className="min-w-0 overflow-hidden">
      {editing ? (
        <input
          ref={inputRef}
          value={value}
          onChange={e => onChange?.(e.target.value)}
          onClick={onInputClick}
          onBlur={() => onSave?.()}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); onSave?.() }
            if (e.key === 'Escape') onCancel?.()
          }}
          className="w-full text-sm border border-charcoal rounded px-2 py-0.5 outline-none"
        />
      ) : (
        <div className="flex items-center gap-2 min-w-0">
          {leading}
          <span
            onClick={onStartEdit}
            className={`text-sm ${editable ? 'cursor-pointer ' : ''}truncate min-w-0 ${isCompleted ? 'line-through' : ''}`}
            style={{ color: isCompleted ? '#648E8A' : '#351E1D' }}
          >
            {item.title}
          </span>
          {isCarriedOver && (
            <span style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '10px',
              fontWeight: 700,
              color: '#B8860B',
              backgroundColor: 'rgba(184,134,11,0.12)',
              padding: '1px 6px',
              borderRadius: '3px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              flexShrink: 0,
            }}>
              Carried Over
            </span>
          )}
          {item.checklist_total_count > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm bg-charcoal/10 text-text-primary flex-shrink-0"
              title={`${item.checklist_completed_count} of ${item.checklist_total_count} checklist items complete`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              {item.checklist_completed_count} of {item.checklist_total_count}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

interface OwnerCellProps {
  item: ActionItem
  members: User[]
  open: boolean
  onToggle: (e: React.MouseEvent) => void
  onClose: () => void
  onSelect: (memberId: number) => void
  triggerRef: React.RefObject<HTMLButtonElement>
  /** false renders the owner as plain text (read-only surfaces). */
  editable?: boolean
  triggerTitle?: string
  menuOnClick?: (e: React.MouseEvent) => void
}

export function ActionItemOwnerCell({
  item, members, open, onToggle, onClose, onSelect, triggerRef,
  editable = true, triggerTitle, menuOnClick,
}: OwnerCellProps) {
  if (!editable) {
    return (
      <div className="relative min-w-0">
        <span className="flex items-center gap-2 text-sm text-text-secondary min-w-0 max-w-full overflow-hidden">
          <ActionItemOwnerAvatar owner={item.owner} />
          <span className="truncate">{item.owner?.full_name || 'Unassigned'}</span>
        </span>
      </div>
    )
  }

  return (
    <div className="relative min-w-0">
      <button
        ref={triggerRef}
        onClick={onToggle}
        title={triggerTitle}
        className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary min-w-0 max-w-full overflow-hidden"
      >
        <ActionItemOwnerAvatar owner={item.owner} />
        <span className="truncate">{item.owner?.full_name || 'Unassigned'}</span>
      </button>
      <FloatingMenu
        open={open}
        onClose={onClose}
        anchorRef={triggerRef}
        width={208}
        align="start"
        onClick={menuOnClick}
        className="bg-white border border-gray-200 rounded-lg shadow-lg w-52 max-h-48 overflow-y-auto"
      >
        {sortPeople(members).map(member => (
          <button
            key={member.id}
            onClick={() => onSelect(member.id)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F8F9FA] text-left"
          >
            <ActionItemOwnerAvatar owner={member} />
            <span className="truncate">{member.full_name}</span>
            {member.id === item.owner?.id && (
              <svg className="ml-auto w-4 h-4 text-[#15532E] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
        ))}
      </FloatingMenu>
    </div>
  )
}

interface DueDateCellProps {
  item: ActionItem
  editing?: boolean
  value?: string
  onChange?: (value: string) => void
  onSave?: () => void
  onCancel?: () => void
  inputRef?: React.RefObject<HTMLInputElement>
  onStartEdit?: (e: React.MouseEvent) => void
  editable?: boolean
  inputAriaLabel?: string
  triggerTitle?: string
  /** Shown when the item has no due date, e.g. "Add due date". */
  emptyLabel?: string
  /** Renders a Clear button beside the date input while editing. */
  onClear?: () => void
  onInputClick?: (e: React.MouseEvent) => void
}

export function ActionItemDueDateCell({
  item, editing = false, value = '', onChange, onSave, onCancel, inputRef,
  onStartEdit, editable = true, inputAriaLabel, triggerTitle, emptyLabel,
  onClear, onInputClick,
}: DueDateCellProps) {
  const style = actionItemDueDateStyle(item)

  const input = (
    <input
      ref={inputRef}
      type="date"
      value={value}
      onChange={e => onChange?.(e.target.value)}
      onBlur={e => {
        // The Clear button lives next to the input; blurring onto it must not
        // save the half-edited value first.
        const next = e.relatedTarget as HTMLElement | null
        if (next && next.dataset?.clearDueDate) return
        onSave?.()
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); onSave?.() }
        if (e.key === 'Escape') onCancel?.()
      }}
      aria-label={inputAriaLabel}
      className="text-sm border border-charcoal rounded px-2 py-0.5 outline-none w-full"
    />
  )

  return (
    <div className="min-w-0 overflow-hidden">
      {editing ? (
        onClear ? (
          <span className="flex items-center gap-1" onClick={onInputClick}>
            {input}
            {item.due_date && (
              <button
                type="button"
                data-clear-due-date="true"
                onClick={onClear}
                title="Clear due date"
                className="text-sm px-1 rounded hover:bg-gray-200 flex-shrink-0"
              >
                Clear
              </button>
            )}
          </span>
        ) : input
      ) : (
        <span
          onClick={onStartEdit}
          title={triggerTitle}
          className={`text-sm ${editable ? 'cursor-pointer ' : ''}truncate block`}
          style={{ color: style.color, opacity: style.opacity }}
        >
          {item.due_date ? formatActionItemDate(item.due_date) : emptyLabel}
        </span>
      )}
    </div>
  )
}

export function ActionItemCreatedDateCell({ item }: { item: ActionItem }) {
  return (
    <time dateTime={item.created_at} className="text-sm text-text-secondary whitespace-nowrap">
      {formatActionItemDate(item.created_at)}
    </time>
  )
}

export function ActionItemSourceCell({ item }: { item: ActionItem }) {
  return (
    <div className="min-w-0 overflow-hidden">
      {item.source_type && (
        <span className="text-sm px-2 py-0.5 rounded-full bg-[#F5F5F5] text-text-secondary truncate inline-block max-w-full align-middle">
          {sourceKindLabel(item.source_type) ?? item.source_type}
        </span>
      )}
    </div>
  )
}
